import React, { useState, useEffect } from 'react';
import { BoothService } from 'app/services/BoothService';
import { useAuth } from 'app/context/auth-context';
import { Button, Modal } from 'react-bootstrap-v5';
import { AppModal } from 'app/modules/shared/modals/AppModal';
import { AlertModal } from 'app/modules/shared/modals/AlertModal';
import { Booth, CreateBoothInput } from 'app/API';
import { useAppContext } from 'app/context/app-context';
import CreateEditBooth from './CreateEditBooth';
import { Amplify, Storage } from 'aws-amplify';
import { VJFPUserService } from 'app/services/VJFPUserService';
import { OrgService } from 'app/services/OrgService';
import { ONE_MIN_IN_MILLISECONDS } from 'app/helpers/MiscUtils';
import { Trash } from 'react-bootstrap-icons';

Amplify.configure({
  Storage: {
    AWSS3: {
      region: 'us-east-1',
      bucket: 'videojobfairs-docs',
    },
  },
});

type ManageBoothsProps = {
  onLoad: (isAnyBoothLoaded: boolean) => void;
  orgOnBoardingProgressPercentage?: number;
  orgName: string;
};

const UnassignedRecruiters: React.FC<{
  recruiterNames: Map<number, string>;
  unassignedRecruiters: number[];
}> = ({ recruiterNames, unassignedRecruiters }) => (
  <div className="bg-white shadow rounded p-3">
    <h6>Unassigned Recruiters</h6>
    <ul className="list-group">
      {unassignedRecruiters.map((recruiter) => (
        <li key={recruiter} className="list-group-item d-flex justify-content-between align-items-center">
          {recruiterNames.get(recruiter) || ''}
        </li>
      ))}
    </ul>
  </div>
);

interface BoothCardProps {
  booth: Booth;
  recruiterNames: Map<number, string>;
  index: number;
  assignRecruiter: (recruiterId: number, boothIndex: number | 'unassigned') => void;
  unAssignedRecruiters: number[];
  handleEdit: (booth: Booth) => void;
  handleDelete: (booth: Booth) => void;
  showDeleteIcon: boolean;
  companyImageUrl?: string;
}

const BoothCard: React.FC<BoothCardProps> = ({
  booth,
  recruiterNames,
  index,
  assignRecruiter,
  unAssignedRecruiters,
  handleEdit,
  handleDelete,
  showDeleteIcon,
  companyImageUrl,
}) => (
  <div className="booth-card">
    <div className="card border border-2 border-gray-300 h-100">
      <div className="card-header border-0 pt-9 ribbon ribbon-end ribbon-clip">
        <div className="card-title">
          <img style={{ maxWidth: '150px' }} src={companyImageUrl} alt={booth.name} crossOrigin="anonymous" />
        </div>
      </div>
      <div className="card-body p-5">
        <div className="fs-3 fw-bold text-dark">{booth.name}</div>
        <p className="text-gray-400 fw-semibold mt-2">{booth.description}</p>
        <div className="d-flex flex-stack mt-3">
          <div className="text-gray-700">Default Interview Duration:</div>
          <div className="fw-bold">{(booth.chatDuration || 0) / ONE_MIN_IN_MILLISECONDS} minutes</div>
        </div>
        <div className="d-flex flex-stack mt-3">
          <div className="text-gray-700">Minimum Score:</div>
          <div className="fw-bold">{booth.minimumScore}</div>
        </div>
        <div className="mt-3 text-gray-700">Recruiters</div>
        <div style={{ border: '1px dashed grey', margin: '5px', minHeight: '50px' }}>
          {booth.recruiters?.map((recruiter) => (
            <div key={recruiter} className="d-flex justify-content-between align-items-center p-1">
              {recruiterNames.get(recruiter!) || ''}
              <Button
                variant="link"
                onClick={() => assignRecruiter(recruiter!, 'unassigned')}
                className="text-danger"
              >
                <Trash />
              </Button>
            </div>
          ))}
          {booth.recruiters?.length === 0 && (
            <div className="text-center text-gray-400">No recruiters assigned</div>
          )}
        </div>
        <select
          className="form-select form-select-sm mt-3"
          onChange={(e) => {
            const recruiterId = parseInt(e.target.value);
            if (!isNaN(recruiterId)) assignRecruiter(recruiterId, index);
          }}
        >
          <option value="">Assign Recruiter</option>
          {unAssignedRecruiters.map((recruiter) => (
            <option key={recruiter} value={recruiter}>
              {recruiterNames.get(recruiter) || ''}
            </option>
          ))}
        </select>
      </div>
      <div className="card-footer d-flex justify-content-between py-3">
        <Button variant="primary" onClick={() => handleEdit(booth)}>
          Manage Booth
        </Button>
        {showDeleteIcon && (
          <Button variant="link" className="text-danger" onClick={() => handleDelete(booth)}>
            <Trash />
          </Button>
        )}
      </div>
    </div>
  </div>
);

const ManageBooths: React.FC<ManageBoothsProps> = (props: ManageBoothsProps) => {
  const [booths, setBooths] = useState<Booth[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [boothToDelete, setBoothToDelete] = useState<Booth | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedBooth, setSelectedBooth] = useState<Booth | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [recruitersNames, setRecruiterNames] = useState(new Map<number, string>());
  const [unAssignedRecruiters, setUnAssignedRecruiters] = useState<number[]>([]);
  const [companyImageUrlMap, setCompanyImageUrlMap] = useState<Map<string, string>>(new Map<string, string>());
  const { user } = useAuth();
  const { jobFair } = useAppContext();

  const boothService = new BoothService();

  const fetchBooths = async (isLoadUsers: boolean = false) => {
    setIsLoading(true);
    if (!jobFair?.id) return;
    const boothList = await boothService.getAllBooths(jobFair.id);
    props.onLoad(boothList.length > 0);

    const _companyImageUrlMap = new Map<string, string>();
    for (const booth of boothList) {
      if (booth.companyImageUrl) {
        _companyImageUrlMap.set(booth.id, await Storage.get(booth.companyImageUrl));
      }
    }
    setCompanyImageUrlMap(_companyImageUrlMap);

    const sortedBooths = boothList.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    if (isLoadUsers && boothList?.length) {
      if (user?.id) {
        const vjfpUserService = new VJFPUserService();
        const fetchedUsers = await vjfpUserService.getUsers(user.organizationId);
        const map = new Map<number, string>();
        fetchedUsers.forEach((user) => {
          map.set(user.id, `${user.firstName} ${user.lastName}`);
        });
        setRecruiterNames(map);

        const assignedRecruiters = new Set<number>(
          boothList.flatMap((x) => x.recruiters || []).filter((id): id is number => id !== null)
        );

        const recruiterIds = Array.from(map.keys());
        const unassignedRecruiterIds = recruiterIds.filter((id) => !assignedRecruiters.has(id));
        setUnAssignedRecruiters(unassignedRecruiterIds);
      }
    }
    setBooths(sortedBooths);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchBooths(true);
  }, [jobFair?.id]);

  const handleCreateBooth = async (values: CreateBoothInput) => {
    if (!user?.id || !jobFair?.id) return;
    values.organizationId = user.organizationId;
    if (selectedBooth?.id) {
      await boothService.updateBooth(selectedBooth.jobFairId, selectedBooth.id, values);
    } else {
      await boothService.createBooth(jobFair?.id, values);
    }

    if (props.orgOnBoardingProgressPercentage && props.orgOnBoardingProgressPercentage < 80) {
      new OrgService().updateOrg(user.organizationId, {
        onBoardingProgress: {
          progress: 80,
          lastCompletedStep: 'BoothConfigured'
        }
      });
    }

    setShowModal(false);
    await fetchBooths();
  };

  const handleDeleteBooth = async (booth: Booth) => {
    setBoothToDelete(booth);
    setShowDeleteAlert(true);
  };

  const handleEditBooth = async (booth: Booth) => {
    setSelectedBooth(booth);
    setShowModal(true);
  };

  const confirmDeleteBooth = async () => {
    if (boothToDelete) {
      await boothService.deleteBooth(boothToDelete.jobFairId, boothToDelete.id);
      setUnAssignedRecruiters((prev) => [...prev]);
    }
    setShowDeleteAlert(false);
    setBoothToDelete(null);
    await fetchBooths();
  };

  const assignRecruiter = async (recruiterId: number, targetBoothIndex: number | 'unassigned') => {
    setIsLoading(true); // Show loading indicator

    const _booths = [...booths];
    const _unassigned = [...unAssignedRecruiters];
    let impactedBoothIndex: number | null = null;

    if (targetBoothIndex === 'unassigned') {
      // Remove recruiter from all booths and add to unassigned
      _booths.forEach((booth, index) => {
        if (booth.recruiters?.includes(recruiterId)) {
          booth.recruiters = booth.recruiters.filter((id) => id !== recruiterId);
          impactedBoothIndex = index;
        }
      });
      _unassigned.push(recruiterId);
    } else {
      // Remove recruiter from unassigned and assign to the specified booth
      const recruiterIndex = _unassigned.indexOf(recruiterId);
      if (recruiterIndex > -1) _unassigned.splice(recruiterIndex, 1);

      // Remove recruiter from any other booth
      _booths.forEach((booth, index) => {
        if (booth.recruiters?.includes(recruiterId)) {
          booth.recruiters = booth.recruiters.filter((id) => id !== recruiterId);
          impactedBoothIndex = index;
        }
      });

      // Assign recruiter to the target booth
      _booths[targetBoothIndex].recruiters = [...(_booths[targetBoothIndex].recruiters || []), recruiterId];
      impactedBoothIndex = targetBoothIndex;
    }

    setBooths(_booths);
    setUnAssignedRecruiters(_unassigned);

    try {
      if (impactedBoothIndex !== null) {
        // Update the impacted booth only
        const impactedBooth = _booths[impactedBoothIndex];
        await boothService.updateBooth(impactedBooth.jobFairId, impactedBooth.id, {
          recruiters: impactedBooth.recruiters?.map((recruiter) => recruiter || null),
        });
      }
    } catch (error) {
      setErrorMessage('Failed to update recruiter assignment. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false); // Hide loading indicator
    }
  };

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">
            {booths.length > 0 ? 'Manage Booths' : 'Booth Registration'}
          </span>
        </h3>
        <div className="card-toolbar">
          {booths.length > 0 && (
            <button
              className="btn btn-lg btn-light-primary"
              onClick={() => {
                setShowModal(true);
                setSelectedBooth(undefined);
              }}
            >
              <i className="ki-duotone ki-plus fs-2"></i>Create New Booth
            </button>
          )}
        </div>
      </div>
      <div className="card-body py-4">
        {isLoading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-row gap-5">
            <div className="w-100 d-flex flex-column gap-5 flex-lg-row flex-wrap align-items-stretch">
              {booths && booths.length > 0 ? (
                booths.map((booth: Booth, index: number) => (
                  <BoothCard
                    key={booth.id}
                    booth={booth}
                    recruiterNames={recruitersNames}
                    index={index}
                    assignRecruiter={assignRecruiter}
                    unAssignedRecruiters={unAssignedRecruiters}
                    handleEdit={handleEditBooth}
                    handleDelete={handleDeleteBooth}
                    showDeleteIcon={booths.length > 1}
                    companyImageUrl={companyImageUrlMap.get(booth.id)}
                  />
                ))
              ) : (
                <div className="w-100 d-flex justify-content-left">
                  <div className="w-50">
                    <CreateEditBooth orgName={props.orgName} onSubmit={handleCreateBooth} />
                  </div>
                </div>
              )}
            </div>
            <UnassignedRecruiters recruiterNames={recruitersNames} unassignedRecruiters={unAssignedRecruiters} />
          </div>
        )}
      </div>

      <Modal size="lg" show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedBooth ? 'Edit Booth' : 'Create New Booth'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateEditBooth
            orgName={props.orgName}
            onSubmit={handleCreateBooth}
            booth={selectedBooth}
            companyImageUrl={selectedBooth?.id ? companyImageUrlMap.get(selectedBooth?.id) : undefined}
          />
        </Modal.Body>
      </Modal>

      <AppModal
        show={showDeleteAlert}
        onSuccess={confirmDeleteBooth}
        onReject={() => setShowDeleteAlert(false)}
        onHide={() => setShowDeleteAlert(false)}
        title="Delete this Booth?"
      >
        Are you sure you want to delete this booth?
      </AppModal>

      <AlertModal show={showErrorModal} onOK={() => setShowErrorModal(false)} title="Error">
        {errorMessage}
      </AlertModal>
    </div>
  );
};

export default ManageBooths;
